import React, { useCallback, useState, useRef, useEffect } from 'react';

import { Icon } from './icon';
import { NewChip } from './new-chip';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './shadcn/tooltip';

interface NaturalLanguageSearchBoxProps {
  value: string;
  processingNL?: boolean;
  entityType: 'facts' | 'documents';

  onChange: (value: string) => void;
  onSearchCall: (value: string, useNaturalLanguage: boolean) => void;
  onNLComplete?: () => void;
  onNLSearchExecuted?: (trigger: 'wand' | 'spacebar') => void;
  clearSearch: () => void;
}

export const NaturalLanguageSearchBox = ({
  value,
  processingNL,
  entityType,
  onChange,
  onSearchCall,
  onNLComplete,
  onNLSearchExecuted,
  clearSearch,
}: NaturalLanguageSearchBoxProps) => {
  const [text, setText] = useState(value);
  const [isNaturalLanguageMode, setIsNaturalLanguageMode] = useState(false);
  const [wasNLQuery, setWasNLQuery] = useState(false);
  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
  const [nlActivationTrigger, setNlActivationTrigger] = useState<'wand' | 'spacebar' | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const onTextChange = useCallback(
    (text: string) => {
      setText(text);
      onChange?.(text);
    },
    [onChange],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ' ' && text === '') {
      e.preventDefault();
      setIsNaturalLanguageMode(true);
      setNlActivationTrigger('spacebar');
      inputRef.current?.focus();
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isNaturalLanguageMode) {
        setWasNLQuery(true);
        // Track NL search execution if trigger is set
        if (nlActivationTrigger) {
          onNLSearchExecuted?.(nlActivationTrigger);
        }
      }
      onSearchCall?.(text, isNaturalLanguageMode);
    }
    if (e.key === 'Escape' && isNaturalLanguageMode) {
      setIsNaturalLanguageMode(false);
      setNlActivationTrigger(null);
      if (text === '') {
        clearSearch?.();
      }
    }
  };

  useEffect(() => {
    setText(value);
  }, [value]);

  useEffect(() => {
    // When NL processing completes, exit NL mode
    if (wasNLQuery && !processingNL) {
      setWasNLQuery(false);
      setNlActivationTrigger(null);
      onNLComplete?.();
    }
  }, [processingNL, wasNLQuery, onNLComplete]);

  useEffect(() => {
    if (isNaturalLanguageMode && !text && !processingNL) {
      const examples = getExamples();
      const currentExample = examples[currentExampleIndex];
      const words = currentExample.split(' ');

      const totalWordsInCurrentExample = words.length;
      const animationDuration = totalWordsInCurrentExample * 150 + 300;
      const displayTime = 2000;

      const timer = setTimeout(() => {
        setCurrentExampleIndex((prevIndex) => (prevIndex + 1) % examples.length);
      }, animationDuration + displayTime);

      return () => clearTimeout(timer);
    }
  }, [isNaturalLanguageMode, currentExampleIndex, text, processingNL, entityType]);

  const getExamples = () => {
    if (entityType === 'facts') {
      return [
        'Find disputed facts about employment issues',
        'Get relevant facts from emails in 2023',
        'Show facts to do with John Smith & Acme Corp',
      ];
    } else {
      return [
        'Show contracts from last month with key facts',
        'Find emails about merger discussions',
        'Get processed documents from 2023 marked relevant',
      ];
    }
  };

  const renderPlaceholder = () => {
    if (text || processingNL) return null;

    if (isNaturalLanguageMode) {
      const examples = getExamples();
      const currentExample = examples[currentExampleIndex];
      const words = currentExample.split(' ');

      return (
        <div className="pointer-events-none absolute top-1/2 left-11 -translate-y-1/2 whitespace-pre text-gray-500">
          {words.map((word, index) => (
            <span
              key={`${currentExampleIndex}-${index}`}
              className="inline-block opacity-0"
              style={{
                animation: `fadeIn 0.3s ease-out forwards`,
                animationDelay: `${index * 150}ms`,
              }}
            >
              {word}
              {index < words.length - 1 ? ' ' : ''}
            </span>
          ))}
        </div>
      );
    }

    return (
      <div className="pointer-events-none absolute top-1/2 left-11 -translate-y-1/2 text-gray-500">
        Search {entityType}. Press
        <kbd className="mx-1.5">Space</kbd>
        to filter {entityType} by describing what you're looking for in plain English.
      </div>
    );
  };

  return (
    <div className="flex items-center gap-2">
      {!processingNL && !isNaturalLanguageMode && (
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button
                className="relative flex h-10.5 w-11.5 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-400 transition-colors hover:border-blue-400 hover:text-blue-500"
                onClick={() => {
                  setIsNaturalLanguageMode(true);
                  setNlActivationTrigger('wand');
                  inputRef.current?.focus();
                }}
              >
                <Icon className="lucide--wand-sparkles h-4 w-4" />
                <NewChip className="-top-2.5 -right-1.75" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={4}>
              <div className="max-w-sm font-medium">Press to filter by describing what you're looking for</div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      <div
        className={`relative flex w-full flex-row items-center justify-center rounded-md border ${
          isNaturalLanguageMode ? 'border-blue-400 bg-blue-50/50' : 'border-gray-300 bg-white'
        } pl-3 text-sm transition-all duration-200`}
        data-testid="natural-language-search-bar"
      >
        <div className="flex items-center gap-2 pl-1">
          {processingNL ? (
            <Icon className="lucide--loader-circle h-4 w-4 animate-spin text-blue-500" />
          ) : isNaturalLanguageMode ? (
            <Icon className="lucide--wand-sparkles h-4 w-4 text-blue-500" />
          ) : (
            <Icon className="lucide--search h-4 w-4 text-gray-400" />
          )}
        </div>

        <input
          ref={inputRef}
          className={`h-10 w-full rounded-md bg-transparent pl-3 font-normal text-black not-italic placeholder:text-gray-500 focus:outline-none ${
            isNaturalLanguageMode ? 'placeholder:text-blue-600/60' : ''
          }`}
          onChange={(e) => onTextChange(e.target.value)}
          onKeyDown={handleKeyDown}
          value={text}
          disabled={processingNL}
        />

        {renderPlaceholder()}

        {clearSearch && (text || isNaturalLanguageMode) && (
          <button
            className={`mr-4 cursor-pointer text-gray-400 transition-opacity duration-500 ${
              value || isNaturalLanguageMode ? 'opacity-100' : 'opacity-0'
            }`}
            onClick={() => {
              clearSearch();
              setText('');
              setIsNaturalLanguageMode(false);
              setWasNLQuery(false);
              setNlActivationTrigger(null);
            }}
          >
            <Icon className="lucide--x h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};
